import { ButtonBase } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { AI_GRADIENT_CSS, AI_GRADIENT_STOPS } from 'common/constants/ai';
import { EventType, useEventBus } from 'context';
import { usePendingReviewCount, useReviewDrawerOpen, useReviewStore } from 'store/useReviewStore';

const AI_SPARKLE_GRADIENT_ID = 'pending-review-badge-sparkle-gradient';

// Scalar AI sparkle, filled with the shared AI purple -> blue gradient.
const AiSparkleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <defs>
      <linearGradient id={AI_SPARKLE_GRADIENT_ID} x1="0" y1="0" x2="1" y2="0">
        {AI_GRADIENT_STOPS.map(stop => (
          <stop key={stop.offset} offset={stop.offset} stopColor={stop.color} />
        ))}
      </linearGradient>
    </defs>
    {/* White disc so the gradient artwork stays visible on the gradient pill. */}
    <circle cx="8" cy="8" r="7.5" fill="white" />
    <path
      d="M8 0C12.4183 0 16 3.58172 16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0ZM8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1ZM4.76465 10.3496C4.76466 10.8081 5.11309 11.185 5.55957 11.2305L5.65039 11.2354V11.4639C5.16135 11.4639 4.76485 11.8606 4.76465 12.3496H4.53613L4.53125 12.2588C4.48563 11.8126 4.10857 11.4641 3.65039 11.4639V11.2354L3.74121 11.2305C4.18746 11.1848 4.53612 10.8079 4.53613 10.3496H4.76465ZM8.52539 3.59961C8.52539 5.73971 10.2603 7.47461 12.4004 7.47461V8.47461C10.2604 8.47461 8.52559 10.2097 8.52539 12.3496H7.52539C7.52519 10.2098 5.79017 8.47485 3.65039 8.47461V7.47461C5.79029 7.47437 7.52539 5.73957 7.52539 3.59961H8.52539ZM4.65039 10.8379C4.54156 11.0603 4.36107 11.2407 4.13867 11.3496C4.36089 11.4584 4.54147 11.6382 4.65039 11.8604C4.75924 11.6386 4.93928 11.4584 5.16113 11.3496C4.93887 11.2407 4.75917 11.0602 4.65039 10.8379ZM11.5146 3.59961C11.5147 4.05809 11.8631 4.43498 12.3096 4.48047L12.4004 4.48535V4.71387C11.9113 4.71387 11.5148 5.11061 11.5146 5.59961H11.2861L11.2812 5.50879C11.2356 5.0626 10.8586 4.7141 10.4004 4.71387V4.48535L10.4912 4.48047C10.9375 4.43476 11.2861 4.05793 11.2861 3.59961H11.5146ZM11.4004 4.08789C11.2916 4.31035 11.1111 4.49068 10.8887 4.59961C11.1109 4.70838 11.2915 4.88822 11.4004 5.11035C11.5092 4.88856 11.6893 4.70836 11.9111 4.59961C11.6889 4.49066 11.5092 4.31025 11.4004 4.08789Z"
      fill={`url(#${AI_SPARKLE_GRADIENT_ID})`}
    />
  </svg>
);

// AI-gradient pill sized like the Documents-tab citation chip; no ripple or animation.
const badgeSx: SxProps<Theme> = {
  flexShrink: 0,
  gap: 0.5,
  mr: 1.5,
  px: 0.75,
  height: '20px',
  borderRadius: '10px',
  background: AI_GRADIENT_CSS,
  color: 'common.white',
  fontWeight: 700,
  fontSize: '0.75rem',
  lineHeight: 1,
  whiteSpace: 'nowrap',
  '&:hover': { filter: 'brightness(0.9)' },
};

/**
 * Pending-review count in the drawer's tab row that hides or shows the review panel.
 * Always shown to reviewers, at 0 when nothing is pending, so the panel can always be toggled.
 */
const PendingReviewBadge = () => {
  const count = usePendingReviewCount();
  const paneVisible = useReviewDrawerOpen();
  const setPaneCollapsed = useReviewStore(s => s.setPaneCollapsed);
  const { publish } = useEventBus();

  const handleClick = (event: React.MouseEvent) => {
    // Keep the minimized drawer header's own click from undoing the expand below.
    event.stopPropagation();
    if (paneVisible) {
      setPaneCollapsed(true);
    } else {
      setPaneCollapsed(false);
      publish(EventType.EXPAND_DRAWER, undefined);
    }
  };

  return (
    <ButtonBase
      disableRipple
      data-testid="pending-review-badge"
      aria-pressed={paneVisible}
      aria-label={`${count} pending review - toggle review panel`}
      title={paneVisible ? 'Hide review panel' : 'Show review panel'}
      sx={badgeSx}
      onClick={handleClick}>
      <AiSparkleIcon />
      {count > 99 ? '99+' : count}
    </ButtonBase>
  );
};

export default PendingReviewBadge;
